import { Typography, Autocomplete, TextField, Grid, Box } from "@mui/material";
import { PropertyWithExamples } from "../ImportTable";
import { FeatureCollection, Geometry, GeoJsonProperties } from "geojson";
import { useMemo, useEffect, useState } from "react";
import { FilterType } from "../../../utilities/types";

export interface PropertyMappings {
	grower: string | null;
	farm: string | null;
	field: string | null;
	fieldId: string | null;
	uniqueKey?: string | null;
}

interface PropertyMappingSelectorProps {
	geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[];
	propertyMappings: PropertyMappings;
	onMappingChange: (mappings: PropertyMappings) => void;
	filterType?: FilterType | null;
}

export default function PropertyMappingSelector({
	geoJsons,
	propertyMappings,
	onMappingChange,
	filterType,
}: PropertyMappingSelectorProps) {
	// State to track if auto-guessing has occurred
	const [showAutoGuessNotice, setShowAutoGuessNotice] = useState(false);

	// Extract properties from the provided GeoJSON data
	const headerProperties = useMemo(() => {
		if (!geoJsons || geoJsons.length === 0) return [];
		return extractAllPropertiesFromGeoData(geoJsons);
	}, [geoJsons]);

	// Property extraction function
	function extractAllPropertiesFromGeoData(
		geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
	): Array<{ name: string; examples: string[] }> {
		const propertyMap = new Map<string, Set<string>>();

		// Collect unique values for each property
		for (const geoJson of geoJsons) {
			for (const feature of geoJson.features || []) {
				if (feature.properties) {
					Object.entries(feature.properties).forEach(([key, value]) => {
						if (value !== null && value !== undefined && String(value).trim() !== "") {
							if (!propertyMap.has(key)) {
								propertyMap.set(key, new Set<string>());
							}
							// Add the string representation of the value
							propertyMap.get(key)!.add(String(value));
						}
					});
				}
			}
		}

		// Convert to array format with up to 3 examples per property
		const result: Array<{ name: string; examples: string[] }> = [];
		for (const [propertyName, values] of propertyMap) {
			const examples = Array.from(values).slice(0, 3);
			result.push({ name: propertyName, examples });
		}

		// Sort by property name
		return result.sort((a, b) => a.name.localeCompare(b.name));
	}

	// Generate property name variants for auto-guessing (moved from ImportForm_GeoData)
	function generatePropertyVariants(property: string, baseTerms: string[]): string[] {
		const variants: string[] = [];
		baseTerms.forEach((term) => {
			if (!term.toLowerCase().startsWith(property)) {
				variants.push(
					property + "" + term, // proopertyterm
					property + " " + term, // property term
					property + "_" + term, // property_term
					property + "-" + term, // property-term
					(property + "" + term).toUpperCase(), // PROPERTYTERM
					(property + " " + term).toUpperCase(), // PROPERTY TERM
					(property + "_" + term).toUpperCase(), // PROPERTY_TERM
					(property + "-" + term).toUpperCase(), // PROPERTY-TERM
					capitalizeFirstLetter(property) + capitalizeFirstLetter(term), // PropertyTerm
					capitalizeFirstLetter(property) + " " + capitalizeFirstLetter(term), // Property Term
					capitalizeFirstLetter(property) + "_" + capitalizeFirstLetter(term), // Property_Term
					capitalizeFirstLetter(property) + "-" + capitalizeFirstLetter(term), // Property-Term
				);
			}

			function capitalizeFirstLetter(str: string) {
				return str.charAt(0).toUpperCase() + str.slice(1);
			}

			function generateTermVariants(term: string): string[] {
				// Split camelCase/PascalCase to words
				const words = term.replace(/([a-z])([A-Z])/g, "$1 $2");

				return [
					term,
					term.toLowerCase(),
					term.toUpperCase(),
					noSpaces(term), // "growername"
					kebabCase(term), // "grower-name"
					kebabCase(words), // "grower-name"
					titleCase(term), // "Grower Name"
					titleCase(words), // "Grower Name"
					pascalCase(term), // GrowerName
					pascalCase(words), // Grower Name
					camelCase(term), // growerName
					camelCase(words), // grower Name
					capitalizeFirstWord(term), // "Grower name"
					capitalizeFirstWord(words), // "Grower name"
					capitalizeLastWord(term), // "grower Name"
					capitalizeLastWord(words), // "grower Name"
					snakeCase(term), // grower_name
					snakeCase(words), // grower_name
					snakeCase(term).toUpperCase(), // GROWER_NAME
					snakeCase(words).toUpperCase(), // GROWER_NAME
				];

				function capitalizeLastWord(str: string): string {
					return str
						.split(" ")
						.map((w, i) =>
							i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
						)
						.join(" ");
				}

				function capitalizeFirstWord(str: string): string {
					return str.charAt(0).toUpperCase() + str.slice(1);
				}

				function noSpaces(str: string): string {
					return str.replace(/\s+/g, "");
				}

				function titleCase(str: string): string {
					return str.replace(/\b\w/g, (c) => c.toUpperCase());
				}

				function camelCase(str: string): string {
					return str
						.split(" ")
						.map((w, i) =>
							i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
						)
						.join("");
				}

				function pascalCase(str: string): string {
					return str
						.split(" ")
						.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
						.join("");
				}

				function snakeCase(str: string): string {
					return str
						.split(" ")
						.map((w) => w.toLowerCase())
						.join("_");
				}

				function kebabCase(str: string): string {
					return str
						.split(" ")
						.map((w) => w.toLowerCase())
						.join("-");
				}
			}

			variants.push(...generateTermVariants(term));

			// Capitalize the first letter of the first word and capitalize the remaining words
			term = term
				.split(" ")
				.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w.toUpperCase()))
				.join(" ");

			variants.push(...generateTermVariants(term));
			variants.push(...generateTermVariants(property));
		});

		// Remove duplicates
		return Array.from(new Set(variants));
	}

	// Extract property name from GeoData based on possible names (moved from ImportForm_GeoData)
	function extractPropertyFromGeoData(
		geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
		possibleNames: string[],
	): string {
		let features: any[] = [];
		for (const geoJson of geoJsons) {
			features = features.concat(geoJson.features || []);
		}

		for (const feature of features || []) {
			if (feature.properties) {
				for (const propertyName of possibleNames) {
					if (propertyName in feature.properties && feature.properties[propertyName]) {
						// Return the property name itself, not its value
						return propertyName;
					}
				}
			}
		}

		return "";
	}

	// Auto-guess property mappings when filter type changes
	useEffect(() => {
		if (!geoJsons || geoJsons.length === 0) return;

		// Clear mappings if filter type is not "Boundary"
		if (filterType !== "Boundary") {
			const hasNonEmptyMappings =
				propertyMappings.grower || propertyMappings.farm || propertyMappings.field || propertyMappings.fieldId;

			if (hasNonEmptyMappings) {
				const clearedMappings: PropertyMappings = {
					grower: null,
					farm: null,
					field: null,
					fieldId: null,
				};
				onMappingChange(clearedMappings);
			}
			// Hide auto-guess notice when switching away from Boundary
			setShowAutoGuessNotice(false);
			return;
		}

		// Only auto-guess if mappings are currently empty/null
		const hasEmptyMappings =
			!propertyMappings.grower && !propertyMappings.farm && !propertyMappings.field && !propertyMappings.fieldId;

		if (!hasEmptyMappings) return;

		// Generate possible property names for each field type
		const possibleFieldPropertyNames = generatePropertyVariants("field", ["name", "title", "field map"]);
		const possibleFarmPropertyNames = generatePropertyVariants("farm", ["farm name"]);
		const possibleGrowerPropertyNames = generatePropertyVariants("grower", ["grower name", "client", "client name"]);
		const possibleFieldIdPropertyNames = generatePropertyVariants("fieldId", ["id", "field key", "field guid"]);

		// Extract the best matching property for each field type
		const guessedField = extractPropertyFromGeoData(geoJsons, possibleFieldPropertyNames);
		const guessedFarm = extractPropertyFromGeoData(geoJsons, possibleFarmPropertyNames);
		const guessedGrower = extractPropertyFromGeoData(geoJsons, possibleGrowerPropertyNames);
		const guessedFieldId = extractPropertyFromGeoData(geoJsons, possibleFieldIdPropertyNames);

		// Create the auto-guessed mappings
		const autoGuessedMappings: PropertyMappings = {
			field: guessedField || null,
			farm: guessedFarm || null,
			grower: guessedGrower || null,
			fieldId: guessedFieldId || null,
		};

		// Only update if we found at least one mapping
		const hasGuesses =
			autoGuessedMappings.field ||
			autoGuessedMappings.farm ||
			autoGuessedMappings.grower ||
			autoGuessedMappings.fieldId;

		if (hasGuesses) {
			onMappingChange(autoGuessedMappings);
			// Show the auto-guess notice
			setShowAutoGuessNotice(true);
		}
	}, [geoJsons, propertyMappings, onMappingChange, filterType]);
	const handleMappingChange = (field: keyof PropertyMappings, value: string | PropertyWithExamples | null) => {
		let newValue: string | null = null;
		if (typeof value === "string") {
			newValue = value;
		} else if (value && typeof value === "object" && "name" in value) {
			newValue = value.name;
		}
		onMappingChange({ ...propertyMappings, [field]: newValue });
		// Hide the auto-guess notice when user manually changes any mapping
		setShowAutoGuessNotice(false);
	};

	const renderAutocomplete = (field: keyof PropertyMappings, label: string) => {
		const selectedProperty = headerProperties?.find((p) => p.name === propertyMappings[field]);

		return (
			<Box>
				<Autocomplete
					options={headerProperties || []}
					value={selectedProperty || null}
					onChange={(_, value) => handleMappingChange(field, value)}
					getOptionLabel={(option) => {
						if (typeof option === "string") return option;
						return option.name;
					}}
					renderOption={(props, option) => (
						<Box component="li" {...props}>
							<Box>
								<Typography variant="body2">{typeof option === "string" ? option : option.name}</Typography>
								{typeof option === "object" && option.examples && option.examples.length > 0 && (
									<Box>
										{option.examples.map((example, index) => (
											<Typography key={index} variant="caption" color="text.secondary" sx={{ display: "block" }}>
												{example}
											</Typography>
										))}
									</Box>
								)}
							</Box>
						</Box>
					)}
					renderInput={(params) => <TextField {...params} label={label} size="small" />}
					clearOnEscape
					clearText="Clear selection"
					isOptionEqualToValue={(option, value) => {
						if (typeof option === "string" && typeof value === "string") return option === value;
						if (typeof option === "object" && typeof value === "string") return option.name === value;
						if (typeof option === "string" && typeof value === "object") return option === value.name;
						if (typeof option === "object" && typeof value === "object") return option.name === value.name;
						return false;
					}}
				/>
				{selectedProperty && selectedProperty.examples && selectedProperty.examples.length > 0 && (
					<Box sx={{ mt: 0.5, ml: 1.5 }}>
						<Typography variant="caption" color="text.secondary">
							{selectedProperty.examples.slice(0, 3).map((example, idx) => (
								<p key={idx} style={{ fontSize: "0.8em", color: "gray", margin: 0 }}>
									{example}
								</p>
							))}
						</Typography>
					</Box>
				)}
			</Box>
		);
	};

	return (
		<>
			<Typography variant="h6" gutterBottom>
				Map File Properties to Columns
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Select which file properties should populate each column
			</Typography>
			{showAutoGuessNotice && (
				<Typography variant="body2" sx={{ mb: 2, color: "success.main", fontStyle: "italic" }}>
					We&apos;ve automatically guessed some property mappings for you. Please review and adjust as needed.
				</Typography>
			)}
			<Grid container spacing={2}>
				<Grid item xs={12} md={6} lg={3}>
					{renderAutocomplete("grower", "Grower")}
				</Grid>
				<Grid item xs={12} md={6} lg={3}>
					{renderAutocomplete("farm", "Farm")}
				</Grid>
				<Grid item xs={12} md={6} lg={3}>
					{renderAutocomplete("field", "Field")}
				</Grid>
				<Grid item xs={12} md={6} lg={3}>
					{renderAutocomplete("fieldId", "Field ID")}
				</Grid>
			</Grid>
		</>
	);
}
